import {
  BaseField,
  Button,
  Card,
  CardContent,
  InputForm,
  Paragraph,
} from "@pocketsign/design-system";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import * as v from "valibot";
import VisibilityOff from "~icons/material-symbols/visibility-off-outline";
import Visibility from "~icons/material-symbols/visibility-outline";
import {
  COMPANY_TOKEN_STORAGE_KEY,
  redirectIfAuthenticated,
} from "../../libs/auth";
import { createAccountClient } from "../../libs/client";
import {
  createPasswordLoginSchema,
  getFieldErrorMessage,
} from "./-schemas/password-login";

export const Route = createFileRoute("/login/company")({
  beforeLoad: async () => {
    redirectIfAuthenticated();
  },
  component: RouteComponent,
});

type CompanyLoginPageProps = {
  login?: (input: { password: string; slug: string }) => Promise<{ token: string }>;
  onSuccess?: (token: string) => void;
};

export function CompanyLoginPage({
  login = async ({ password, slug }) => {
    const client = createAccountClient();
    return client.login({ password, slug });
  },
  onSuccess = (token) => {
    localStorage.setItem(COMPANY_TOKEN_STORAGE_KEY, token);
    window.location.assign("/company/dashboard");
  },
}: CompanyLoginPageProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const loginSchema = useMemo(
    () =>
      createPasswordLoginSchema({
        loginIdRequired: t("common.validation.loginIdRequired"),
        passwordRequired: t("common.validation.passwordRequired"),
      }),
    [t],
  );
  const form = useForm({
    defaultValues: {
      loginId: "",
      password: "",
    },
    validators: {
      onChange: loginSchema,
      onSubmit: loginSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const normalizedValue = v.parse(loginSchema, value);
        const res = await login({
          slug: normalizedValue.loginId,
          password: normalizedValue.password,
        });

        if (res.token) {
          onSuccess(res.token);
        }
      } catch (error) {
        console.error(error);
        toast.error(t("common.errors.loginFailed"));
      }
    },
  });

  return (
    <div
      data-panda-theme="v2"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#F3F4F6",
        padding: "16px",
      }}
    >
      <div style={{ width: "100%", maxWidth: "440px" }}>
        <Card>
          <CardContent>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  textAlign: "start",
                }}
              >
                <div style={{ fontSize: "18px", fontWeight: 600 }}>
                  {t("login.company.title")}
                </div>
                <Paragraph color="secondary" size="sm">
                  {t("login.company.description")}
                </Paragraph>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void form.handleSubmit();
                }}
                noValidate
                autoComplete="on"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <form.Field
                    name="loginId"
                    children={(field) => (
                      <InputForm
                        id="loginId"
                        name={field.name}
                        label={t("common.fields.loginId")}
                        size="md"
                        required
                        autoComplete="username"
                        value={field.state.value}
                        error={getFieldErrorMessage(field.state.meta.errors)}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    )}
                  />

                  <form.Field
                    name="password"
                    children={(field) => {
                      const errorMessage = getFieldErrorMessage(
                        field.state.meta.errors,
                      );

                      return (
                        <BaseField
                          id="password"
                          label={t("common.fields.password")}
                          required
                          error={errorMessage}
                        >
                          <div style={{ position: "relative" }}>
                            <input
                              type={showPassword ? "text" : "password"}
                              id="password"
                              name={field.name}
                              required
                              autoComplete="current-password"
                              value={field.state.value}
                              onBlur={field.handleBlur}
                              onChange={(e) => field.handleChange(e.target.value)}
                              style={{
                                width: "100%",
                                height: "48px",
                                borderRadius: "12px",
                                border: errorMessage
                                  ? "1px solid #DC2626"
                                  : "1px solid #D1D5DB",
                                padding: "12px 48px 12px 16px",
                                fontSize: "14px",
                                background: "#FFFFFF",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={
                                showPassword
                                  ? t("common.password.hide")
                                  : t("common.password.show")
                              }
                              style={{
                                position: "absolute",
                                right: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                background: "transparent",
                                border: "none",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#6B7280",
                              }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </button>
                          </div>
                        </BaseField>
                      );
                    }}
                  />
                </div>

                <form.Subscribe
                  selector={(state) => state.isSubmitting}
                  children={(isSubmitting) => (
                    <Button type="submit" disabled={isSubmitting} size="lg">
                      {isSubmitting
                        ? t("common.actions.loggingIn")
                        : t("common.actions.login")}
                    </Button>
                  )}
                />
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RouteComponent() {
  const navigate = useNavigate();

  return (
    <CompanyLoginPage
      onSuccess={(token) => {
        localStorage.setItem(COMPANY_TOKEN_STORAGE_KEY, token);
        navigate({ to: "/company/dashboard" });
      }}
    />
  );
}
